import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdCheckCircle, MdRefresh, MdSend } from 'react-icons/md';
import LeadDebugButton from './LeadDebugButton';

// Onglet « Bloqués historiques » : leads bloqués jamais proposés à Batiweb (arrivés avant les règles
// de blocage Batiweb, ou refusés par elles), que les règles actuelles acceptent et dont le produit a
// un worktype. Filtre par produit, envoi un par un ou en lot : chaque lead est revérifié côté
// serveur, marqué envoyable (il reste bloqué pour les acheteurs) puis envoyé comme un no-match.

interface LeadContact {
    firstname: string | null;
    lastname: string | null;
    email: string | null;
    phone: string | null;
    zipcode: string | null;
    city: string | null;
}

interface BlockedLead {
    _id: string;
    createdAt: string;
    contact: LeadContact;
    seller: string | null;
    invalidReason: string | null;
    industry: string;
    subIndustry: string;
    worktypeId: number;
    worktypeLabel: string | null;
}

interface Category {
    industry: string;
    subIndustry: string;
    count: number;
}

type Skipped = { other_block: number; excluded_product: number; no_product: number; no_worktype: number };

const SKIPPED_LABELS: Record<keyof Skipped, string> = {
    other_block: 'réponse bloquée non acceptée',
    excluded_product: 'produit exclu',
    no_product: 'produit inconnu',
    no_worktype: 'produit sans worktype',
};

const PERIODS = [
    { days: 30, label: '30 derniers jours' },
    { days: 90, label: '3 derniers mois' },
    { days: 180, label: '6 derniers mois' },
    { days: 365, label: '12 derniers mois' },
    { days: 730, label: '24 derniers mois' },
];

// "Réponse bloquée: Blocked answer 'Locataire' for field 'Propriétaire' (global)" -> "Propriétaire : Locataire"
const blockLabel = (reason: string | null) => {
    const match = reason?.match(/Blocked answer '(.+)' for field '(.+)' \(/);
    return match ? `${match[2]} : ${match[1]}` : reason || '—';
};

type RowResult = { ok: boolean; message: string };

export default function BlockedLeadsTab() {
    const [leads, setLeads] = useState<BlockedLead[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [skipped, setSkipped] = useState<Skipped | null>(null);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [days, setDays] = useState(90);
    const [industry, setIndustry] = useState('');
    const [subIndustry, setSubIndustry] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [busyId, setBusyId] = useState<string | null>(null);
    const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
    const [results, setResults] = useState<Record<string, RowResult>>({});
    // Leads traités (importés, refusés par Batiweb, attribués au waterfall) : plus envoyables.
    const [done, setDone] = useState<Set<string>>(new Set());
    const [banner, setBanner] = useState('');

    const fetchLeads = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const res = await axios.get(`${CONFIG.apiUrl}/admin/batiweb/blocked-leads`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                params: { days, industry: industry || undefined, subIndustry: subIndustry || undefined },
            });
            setLeads(res.data.leads);
            setCategories(res.data.categories);
            setSkipped(res.data.skipped);
            setTruncated(res.data.truncated);
            setSelected(new Set());
            setResults({});
            setDone(new Set());
        } catch (e: any) {
            setError(e.response?.data?.message || 'Impossible de charger les leads bloqués.');
        } finally {
            setLoading(false);
        }
    }, [days, industry, subIndustry]);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    const industries = useMemo(() => Array.from(new Set(categories.map((c) => c.industry))), [categories]);
    const subIndustries = useMemo(() => categories.filter((c) => c.industry === industry), [categories, industry]);

    const selectable = useMemo(() => leads.filter((l) => !done.has(l._id)), [leads, done]);
    const selectedLeads = useMemo(() => selectable.filter((l) => selected.has(l._id)), [selectable, selected]);
    const allSelected = selectable.length > 0 && selectedLeads.length === selectable.length;

    const toggleSelected = (id: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map((l) => l._id)));

    const leadName = (lead: BlockedLead) => [lead.contact.firstname, lead.contact.lastname].filter(Boolean).join(' ') || lead._id;

    // Renvoie true si le lead est parti chez Batiweb (importé, ou import incertain que le cron tranchera).
    const sendLead = async (lead: BlockedLead): Promise<boolean> => {
        setBusyId(lead._id);
        try {
            const res = await axios.post(`${CONFIG.apiUrl}/admin/batiweb/blocked-leads/${lead._id}/send`, {}, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            const imported = res.data.status === 'pending' || res.data.status === 'sending';
            const message = imported
                ? res.data.status === 'pending' ? 'Importé chez Batiweb (onglet « En cours »)' : res.data.message
                : res.data.waterfall?.ok
                    ? `Refusé par Batiweb, attribué au waterfall : ${res.data.waterfall.buyerEmail}`
                    : `Refusé par Batiweb : ${res.data.message}`;
            setResults((prev) => ({ ...prev, [lead._id]: { ok: imported || !!res.data.waterfall?.ok, message } }));
            setDone((prev) => new Set(prev).add(lead._id));
            return imported;
        } catch (e: any) {
            const message = e.response?.data?.message || "Erreur lors de l'envoi.";
            setResults((prev) => ({ ...prev, [lead._id]: { ok: false, message } }));
            // 409 / 422 : le lead n'est plus envoyable (acheté, déjà envoyé, règles changées).
            if (e.response?.status === 409 || e.response?.status === 422) setDone((prev) => new Set(prev).add(lead._id));
            return false;
        } finally {
            setSelected((prev) => {
                const next = new Set(prev);
                next.delete(lead._id);
                return next;
            });
        }
    };

    const handleSend = async (lead: BlockedLead) => {
        if (!window.confirm(`Envoyer ${leadName(lead)} à Batiweb sur le métier « ${lead.worktypeLabel} » ?\n\nIl reste bloqué pour nos acheteurs.`)) return;
        setBanner('');
        await sendLead(lead);
        setBusyId(null);
    };

    const handleSendSelected = async () => {
        const toSend = selectedLeads;
        if (toSend.length === 0) return;
        if (!window.confirm(`Envoyer ${toSend.length} lead(s) bloqué(s) à Batiweb ?\n\nIls partent un par un et restent bloqués pour nos acheteurs.`)) return;
        setBanner('');
        setBulkProgress({ done: 0, total: toSend.length });
        let imported = 0;
        for (const [i, lead] of toSend.entries()) {
            if (await sendLead(lead)) imported += 1;
            setBulkProgress({ done: i + 1, total: toSend.length });
        }
        setBusyId(null);
        setBulkProgress(null);
        const others = toSend.length - imported;
        setBanner(`${imported}/${toSend.length} lead(s) importé(s) chez Batiweb.${others ? ` ${others} non importé(s) : voir le motif sur chaque ligne.` : ''}`);
    };

    const busy = !!busyId || !!bulkProgress;
    const skippedTotal = skipped ? Object.values(skipped).reduce((a, b) => a + b, 0) : 0;

    return (
        <>
            <div className="mb-4 flex flex-wrap items-end gap-4">
                <div>
                    <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Période</label>
                    <select
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    >
                        {PERIODS.map((p) => (
                            <option key={p.days} value={p.days}>{p.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Industrie</label>
                    <select
                        value={industry}
                        onChange={(e) => {
                            setIndustry(e.target.value);
                            setSubIndustry('');
                        }}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    >
                        <option value="">Toutes</option>
                        {industries.map((name) => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Produit</label>
                    <select
                        value={subIndustry}
                        onChange={(e) => setSubIndustry(e.target.value)}
                        disabled={!industry}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    >
                        <option value="">Tous</option>
                        {subIndustries.map((c) => (
                            <option key={c.subIndustry} value={c.subIndustry}>{c.subIndustry} ({c.count})</option>
                        ))}
                    </select>
                </div>
                <button
                    onClick={fetchLeads}
                    disabled={loading || busy}
                    className="ml-auto flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-2 text-xs font-semibold text-navy-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-navy-700 dark:text-white"
                >
                    <MdRefresh />
                    Actualiser
                </button>
            </div>

            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                Leads bloqués jamais proposés à Batiweb, sans acheteur, que les règles de blocage actuelles accepteraient. Ils restent bloqués
                pour nos acheteurs ; « Envoyer » fait tout le process Batiweb (is_sellable, import), et un lead refusé passe au waterfall.
            </p>

            {skipped && skippedTotal > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-gray-500">Restent bloqués pour Batiweb :</span>
                    {(Object.keys(SKIPPED_LABELS) as (keyof Skipped)[])
                        .filter((key) => skipped[key] > 0)
                        .map((key) => (
                            <span key={key} className="rounded-full bg-gray-100 px-3 py-1 font-semibold text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                                {skipped[key]} {SKIPPED_LABELS[key]}
                            </span>
                        ))}
                </div>
            )}
            {truncated && (
                <div className="mb-4 rounded-lg bg-orange-100 p-3 text-sm text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                    Trop de leads bloqués sur la période : seuls les 5000 plus récents sont analysés. Réduisez la période pour voir les plus anciens.
                </div>
            )}

            {error && <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">{error}</div>}
            {banner && <div className="mb-4 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">{banner}</div>}

            {loading ? (
                <div className="py-10 text-center text-gray-400">Chargement...</div>
            ) : leads.length === 0 ? (
                <div className="py-10 text-center text-gray-400">Aucun lead bloqué à envoyer à Batiweb sur la période.</div>
            ) : (
                <div className="overflow-x-auto">
                    <div className="mb-2 flex items-center gap-3">
                        <span className="text-xs text-gray-400">{leads.length} lead(s) envoyable(s)</span>
                        <button
                            onClick={handleSendSelected}
                            disabled={selectedLeads.length === 0 || busy}
                            className="flex items-center gap-1 whitespace-nowrap rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-40"
                        >
                            <MdSend />
                            {bulkProgress ? `Envoi ${bulkProgress.done}/${bulkProgress.total}...` : `Envoyer la sélection à Batiweb (${selectedLeads.length})`}
                        </button>
                    </div>
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 pr-3">
                                    <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectable.length === 0 || busy} title="Tout sélectionner" />
                                </th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Date</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Lead</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Produit leadrs</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Bloqué pour</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Métier Batiweb</th>
                                <th className="pb-3 text-xs font-bold uppercase text-gray-500">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map((lead) => {
                                const result = results[lead._id];
                                const isDone = done.has(lead._id);
                                return (
                                    <tr key={lead._id} className={`border-b border-gray-100 align-top dark:border-navy-700 ${isDone ? 'opacity-60' : ''}`}>
                                        <td className="py-3 pr-3">
                                            <input type="checkbox" checked={selected.has(lead._id)} onChange={() => toggleSelected(lead._id)} disabled={isDone || busy} />
                                        </td>
                                        <td className="whitespace-nowrap py-3 pr-4 text-xs text-gray-500">
                                            {new Date(lead.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                                        </td>
                                        <td className="py-3 pr-4">
                                            <div className="flex items-center gap-1.5 font-semibold text-navy-700 dark:text-white">
                                                {[lead.contact.firstname, lead.contact.lastname].filter(Boolean).join(' ') || '—'}
                                                <LeadDebugButton leadId={lead._id} onEdited={fetchLeads} />
                                            </div>
                                            <div className="text-xs text-gray-500">{lead.contact.email || '—'} · {lead.contact.phone || '—'}</div>
                                            <div className="text-xs text-gray-500">
                                                {lead.contact.zipcode || 'CP ?'}{lead.contact.city ? ` ${lead.contact.city}` : ''}
                                                {lead.seller && <span className="text-gray-400"> · vendeur {lead.seller}</span>}
                                            </div>
                                        </td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                                            {lead.subIndustry}
                                            <div className="text-xs text-gray-400">{lead.industry}</div>
                                        </td>
                                        <td className="py-3 pr-4 text-xs text-gray-600 dark:text-gray-300">{blockLabel(lead.invalidReason)}</td>
                                        <td className="py-3 pr-4">
                                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                                                {lead.worktypeLabel} ({lead.worktypeId})
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <button
                                                onClick={() => handleSend(lead)}
                                                disabled={isDone || busy}
                                                className="flex items-center gap-1 whitespace-nowrap rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-40"
                                            >
                                                <MdSend />
                                                {busyId === lead._id ? 'Envoi...' : 'Envoyer à Batiweb'}
                                            </button>
                                            {result && (
                                                <div className={`mt-2 flex items-center gap-1 text-xs ${result.ok ? 'text-green-600' : 'text-red-600'}`}>
                                                    {result.ok && <MdCheckCircle />}
                                                    {result.message}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
